import { TextProps, Card } from '@chakra-ui/react';
import {
  CustomerPortalBillGroup,
  CustomerPortalInvoice,
  InvoiceStatusEnum,
} from '@monetize/types/app';
import { formatCurrency, pluralize, toDateShort } from '@monetize/utils/core';
import { ReactNode } from 'react';
import {
  MBox,
  MCard,
  MFlex,
  MGrid,
  MText,
} from '../../../../components/Monetize';
import { BILL_GROUP_INVOICE_FREQUENCY_DISPLAY } from '../../../../constants/billGroups';
import {
  CUSTOMER_PORTAL_TABS,
  CustomerPortalTabs,
} from '../customerPortalUtils';
import { isInvoicePastDue } from '../invoice.utils';

interface AutoPayPillProps {
  isOn: boolean;
}

const AutoPayPill = ({ isOn }: AutoPayPillProps) => (
  <MFlex
    display="inline-flex"
    alignItems="center"
    gap={2}
    px={3}
    py={1}
    borderRadius="full"
    bg={isOn ? 'tGreen.light' : 'bg.muted'}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={isOn ? 'action.success' : 'border.subtle'}
  >
    <MBox
      w={2}
      h={2}
      borderRadius="full"
      bg={isOn ? 'action.success' : 'fg.muted'}
    />
    <MText
      fontSize="sm"
      fontWeight={600}
      color={isOn ? 'action.success' : 'fg.muted'}
    >
      {isOn ? 'On' : 'Off'}
    </MText>
  </MFlex>
);

interface SummaryItemProps {
  label: string;
  value: string | ReactNode;
  subtext?: string;
  subtextProps?: TextProps;
  children?: ReactNode;
}

const SummaryItem = ({
  label,
  value,
  subtext,
  subtextProps,
  children,
}: SummaryItemProps) => (
  <MBox>
    <MText fontSize="xs" color="fg.muted" fontWeight={500} mb={1}>
      {label}
    </MText>
    {typeof value === 'string' ? (
      <MText fontSize="2xl" fontWeight={700} color="fg.strong">
        {value}
      </MText>
    ) : (
      value
    )}
    {subtext && (
      <MText fontSize="xs" color="fg.muted" mt={1} {...subtextProps}>
        {subtext}
      </MText>
    )}
    {children}
  </MBox>
);

interface BillingSummaryCardProps {
  billGroup: CustomerPortalBillGroup;
  invoices: CustomerPortalInvoice[];
  onNavigate: (tab: CustomerPortalTabs) => void;
  canNavigateToInvoices?: boolean;
}

export const BillingSummaryCard = ({
  billGroup,
  invoices,
  onNavigate,
  canNavigateToInvoices = false,
}: BillingSummaryCardProps) => {
  const {
    nextInvoiceDate,
    collectInvoiceBalanceAutomatically,
    paymentMethodId,
    invoicingFrequency,
    currency,
  } = billGroup;

  // Calculate total upcoming payment from unpaid invoices
  const unpaidInvoices = invoices.filter(
    ({ status, amountDue }) =>
      status === InvoiceStatusEnum.UNPAID && amountDue > 0,
  );

  const pastDueInvoices = invoices.filter(isInvoicePastDue);

  // FIXME: this is not correct - BP needs to provide this data point
  const upcomingPayment = unpaidInvoices.reduce(
    (sum, { amountDue }) => sum + amountDue,
    0,
  );

  return (
    <MCard variant="border" borderRadius="md">
      <Card.Body p={5}>
        <MGrid
          templateColumns={{
            base: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          }}
          gap={6}
        >
          <SummaryItem
            label="Next Billing Date"
            value={nextInvoiceDate ? toDateShort(nextInvoiceDate) : 'N/A'}
          />
          <SummaryItem
            label="Outstanding Balance"
            value={
              unpaidInvoices.length > 0
                ? formatCurrency(upcomingPayment, {
                    currency,
                    maximumFractionDigits: 2,
                  })
                : 'No payment due'
            }
            subtext={
              pastDueInvoices.length > 0
                ? `${pastDueInvoices.length} past due ${pluralize(
                    'invoice',
                    pastDueInvoices.length,
                  )}`
                : unpaidInvoices.length > 0
                  ? `${unpaidInvoices.length} unpaid ${pluralize('invoice', unpaidInvoices.length)}`
                  : undefined
            }
            subtextProps={
              pastDueInvoices.length > 0 && canNavigateToInvoices
                ? {
                    color: 'tRed.base',
                    _hover: {
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    },
                    onClick: () => onNavigate(CUSTOMER_PORTAL_TABS.INVOICES),
                  }
                : undefined
            }
          />
          <SummaryItem
            label="Billing Frequency"
            value={BILL_GROUP_INVOICE_FREQUENCY_DISPLAY[invoicingFrequency]}
          />
          <SummaryItem
            label="Auto-pay"
            value={
              <AutoPayPill
                isOn={!!collectInvoiceBalanceAutomatically && !!paymentMethodId}
              />
            }
          />
        </MGrid>
      </Card.Body>
    </MCard>
  );
};
